import { Row, Col, Table, Button, Typography, Space, message, Popconfirm } from "antd";
import { EyeInvisibleOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons"; // Added EyeOutlined
import axios from "axios";
import { useEffect, useState } from "react";
import AddCategoryForm from "../components/AddCategoryForm";
import UpdateCategoryForm from "../components/UpdateCategoryForm";
import { BASE_URL } from "../constants/Constants";

const { Title } = Typography;

const Category = () => {
  const userId = localStorage.getItem("userId");
  const [category, setCategory] = useState([]);
  const [isAddCategoryModalVisible, setIsAddCategoryModalVisible] = useState(false);
  const [isUpdateCategoryModalVisible, setIsUpdateCategoryModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // RESTORED: State to track if we are viewing hidden items
  const [isShowingHiddenCategory, setIsShowingHiddenCategory] = useState(false);

  const showAddCategoryModal = () => {
    setIsAddCategoryModalVisible(true);
  };

  const hideAddCategoryModal = () => {
    setIsAddCategoryModalVisible(false);
  };

  const showUpdateCategoryModal = (category) => {
    setSelectedCategory(category);
    setIsUpdateCategoryModalVisible(true);
  };

  const hideUpdateCategoryModal = () => {
    setSelectedCategory(null);
    setIsUpdateCategoryModalVisible(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsShowingHiddenCategory(false);
      const response = await axios.get(`${BASE_URL}/category/all-unhide`);
      setCategory(response?.data);
    } catch (error) {
      console.log(error);
    }
  };

  // RESTORED: Function to fetch hidden categories
  const fetchHiddenCategories = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/category/all-hide`);
      setCategory(response?.data);
      setIsShowingHiddenCategory(true);
    } catch (error) {
      console.log("Error: ", error.message);
    }
  };

  const handleCategoryHide = async (categoryId) => {
    try {
      await axios.post(`${BASE_URL}/category/hide?categoryId=${categoryId}`);
      message.success("Category hidden");
      fetchData(); 
    } catch (error) {
      console.log("Error: ", error.message);
      message.error("Failed to hide category");
    }
  };

  // RESTORED: Function to Un-hide
  const handleCategoryUnHide = async (categoryId) => {
    try {
      await axios.post(`${BASE_URL}/category/unhide?categoryId=${categoryId}`);
      message.success("Category restored");
      fetchHiddenCategories(); // Refresh hidden list
    } catch (error) {
      console.log("Error: ", error.message);
    }
  };

  const handleDelete = async (categoryId) => {
    try {
      await axios.delete(`${BASE_URL}/category/deleteCategory?categoryId=${categoryId}`);
      message.success("Category deleted forever");
      // Refresh the correct list
      if (isShowingHiddenCategory) fetchHiddenCategories();
      else fetchData();
    } catch (error) {
      console.error("Delete Error", error);
      if (error.response && error.response.status === 409) {
         message.error("Cannot delete: This category contains products.");
      } else {
         message.error("Failed to delete category.");
      }
    }
  };

  const columns = [
    {
      title: "Category Name",
      dataIndex: "categoryName",
      key: "categoryName",
      width: '70%',
    },
    {
      title: "Actions",
      key: "actions",
      width: '30%',
      render: (text, record) => (
        <Space size="large">
            {isShowingHiddenCategory ? (
                // --- HIDDEN VIEW ACTIONS ---
                <>
                   <Button 
                     type="text" 
                     icon={<EyeOutlined style={{ color: "blue", fontSize: '18px' }} />} 
                     onClick={() => handleCategoryUnHide(record.categoryId)}
                   >
                     Unhide
                   </Button>
                   <Popconfirm
                        title="Delete permanently?"
                        onConfirm={() => handleDelete(record.categoryId)}
                        okText="Yes"
                        cancelText="No"
                        okType="danger"
                    >
                        <DeleteOutlined style={{ color: "red", cursor: "pointer", fontSize: '18px' }} />
                    </Popconfirm>
                </>
            ) : (
                // --- NORMAL VIEW ACTIONS ---
                <>
                    <EditOutlined
                        style={{ color: "green", cursor: "pointer", fontSize: '18px' }}
                        onClick={() => showUpdateCategoryModal(record)}
                        title="Edit"
                    />
                    
                    <Popconfirm
                        title="Hide this category?"
                        description="It will be moved to hidden list."
                        onConfirm={() => handleCategoryHide(record.categoryId)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <EyeInvisibleOutlined
                            style={{ color: "orange", cursor: "pointer", fontSize: '18px' }}
                            title="Hide"
                        />
                    </Popconfirm>

                    <Popconfirm
                        title="Delete this category?"
                        description="This cannot be undone."
                        onConfirm={() => handleDelete(record.categoryId)}
                        okText="Yes"
                        cancelText="No"
                        okType="danger"
                    >
                        <DeleteOutlined 
                            style={{ color: "red", cursor: "pointer", fontSize: '18px' }} 
                            title="Delete Forever"
                        />
                    </Popconfirm>
                </>
            )}
        </Space>
      ),
    },
  ];

  return userId ? (
    <>
      <div>
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px"
              }}
            >
              <Title level={3}>
                  {isShowingHiddenCategory ? "Hidden Categories" : "Category Data"}
              </Title>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                  {/* RESTORED: The Button to toggle views */}
                  <Button onClick={isShowingHiddenCategory ? fetchData : fetchHiddenCategories}>
                    {isShowingHiddenCategory ? "Show Active" : "Show Hidden"}
                  </Button>
                  
                  {!isShowingHiddenCategory && (
                    <Button type="primary" onClick={showAddCategoryModal}>Add Category</Button>
                  )}
              </div>
              
              <AddCategoryForm
                visible={isAddCategoryModalVisible}
                onClose={hideAddCategoryModal}
              />
            </div>
          </Col>
        </Row>
      </div>

      <Table
        rowKey="categoryId"
        columns={columns}
        dataSource={category}
        pagination={false}
      />

      {selectedCategory && (
        <UpdateCategoryForm
          visible={isUpdateCategoryModalVisible}
          onClose={hideUpdateCategoryModal}
          category={selectedCategory}
          onUpdate={(updatedCategory) => {
            hideUpdateCategoryModal();
            fetchData(); 
          }}
        />
      )}
    </>
  ) : (
    <span
      style={{
        fontSize: "20px",
        background: "white",
        padding: "20px",
        borderRadius: "10px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
      }}
    >
      Sign in to see Categories
    </span>
  );
};

export default Category;